import * as React from 'react'
import { PassRestrictionsInput, APIClient, constructUrlEncodedString, ClientsideLogger, RequestContext } from '@utils'
import { Modal } from '@amzn/dvui/dist/Modal'
import { Text } from '@amzn/dvui/dist/Text'
import { Button, Primary } from '@amzn/dvui/dist/Button'
import * as st from './Restrictions.module.scss'
import { PinModalContent, OnPinSubmit } from './PinModalContent'
import * as Types from 'DVWebNodeWpsRuntime'
import { WPSClient } from 'DVWebNodeUtils'
import { jsError} from '../../../widgets/DVWebNodeSPA/utils/csm'

const wpsClient = new WPSClient()
const api = new APIClient()
const logger = new ClientsideLogger('DVWebNodeSPA/Restrictions')
const PASS_RESTRICTIONS_CALL_FAILED = 'DVWeb::Download::PassRestrictions::Failed'

type Input = Omit<PassRestrictionsInput, 'context'>
export const callPassRestrictions = async (input: Input, context: RequestContext, submitEndpoint: string = "") => {
  try {
    const response = (submitEndpoint) 
      // use customized POST method instead of WPSClient.passRestrictions because
      // the POST data has to be in POST body instead on URL params, so that
      // Siege can pick it up and encrypt the PIN value
      ? await api.call(submitEndpoint, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-requested-with': 'XMLHttpRequest' // must be there for WPS not to do the redirect
          },
          method: 'POST',
          body: constructUrlEncodedString(logger, input)
        })
      : await wpsClient.PassRestrictions({context, ...input})

    logger.count(PASS_RESTRICTIONS_CALL_FAILED, 0, true)

    return response
    
  } catch (e) {
    logger.count(PASS_RESTRICTIONS_CALL_FAILED, 1, true)
    jsError(e, 'Error calling PassRestrictions')
  }
}

type RestrictionModalProps = {
  modal: Types.atv.wps.RestrictionModalProps, 
  closeModal: () => void,
  onPinSubmit: OnPinSubmit,
}

type ButtonProps = Types.atv.wps.RestrictionModalLinkButtonProps | Types.atv.wps.RestrictionModalCloseButtonProps

export const RestrictionModal = (props: RestrictionModalProps) => {
  const restrictionModal = React.createRef<Modal>()
  const {modal, onPinSubmit, closeModal} = props

  if (!modal) {
    return null
  }

  const hasButtons = modal && modal.buttons && modal.buttons.length
  const footer = !hasButtons ? null : (
    <>
      {modal.buttons.map((buttonProps, i) => <ActionButton key={`action-btn-${i}`} {...buttonProps as ButtonProps} closeModal={closeModal} />)}
    </>
  )

  const textContent = (modal.content as Types.atv.wps.TextModalContentProps).text
  const pinContent = {
    ...(modal.content as Types.atv.wps.PinModalContentProps),
    onPinCancel: closeModal,
    onPinSubmit,
  }

  return (
    <Modal
      isVisible={true}
      className={st.modal}
      modalId={`restriction-modal`}
      ref={restrictionModal}
      footer={footer}
      header={<h3 className={st.header}>{modal.title}</h3>}
      onClose={props.closeModal}
    >
      {textContent
        ? <Text {...textContent} />
        : <PinModalContent {...pinContent} />
      }
    </Modal>
  )

}

type LinkButtonProps = {
  className: string,
  href: string,
  children: string,
  use?: typeof Primary,
}
type CloseButtonProps = {
  className: string,
  children: string,
  onClick: () => void,
}
type ActionPayload = Types.atv.wps.RestrictionModalLinkPayloadProps | Types.atv.wps.RestrictionModalClosePayloadProps
const buttonPropsByAction = {
  LINK: (payload: ActionPayload): LinkButtonProps => ({
    className: st.actionButton,
    use: payload.isPrimary ? Primary : undefined,
    href: (payload as Types.atv.wps.RestrictionModalLinkPayloadProps).href,
    children: payload.label
  }),
  CLOSE: (payload: ActionPayload, closeModal: () => void): CloseButtonProps => ({
    className: st.actionButton,
    children: payload.label,
    onClick: () => {closeModal && closeModal()}
  }),
}

type ActionButtonProps = ButtonProps & {closeModal: () => void }
const ActionButton = ({action, payload, closeModal}: ActionButtonProps) => {

  const getProps = buttonPropsByAction[action as keyof typeof buttonPropsByAction]

  if (!getProps) {
    jsError(new Error('PassRestrictions Unexpected Action'), `Unexpected Action (${action}) returned from PassRestrictions`)
    return null
  }

  return (
    <Button {...getProps(payload, closeModal)} />
  )
}